import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { withForm } from '~/utils/withForm';
import { Button } from '~/components';
import { Form, TextArea } from 'semantic-ui-react';
import { createPortfolio } from '~/api/portfolios';
import { getInstallers } from '~/api/admin';
import { AddProjectsDropdown, getAvailableProjectOptions } from '~/components';
import Modal from '~/components/Modal';
import { FormSchemaAwareWidget } from '~/components/form';
import { useForm } from '~/hooks/providers/FormContextProvider';
import { useUserContext } from '~/hooks';
import { useProjectList } from '~/requests/projects/useProjectList';
import { Project } from '~/types/project';
interface CreatePortfolioFormState {
  name: string;
  description: string;
  projectIds?: string[];
}

const defaults: CreatePortfolioFormState = { name: '', description: '' };

const CreatePortfolioModal = withForm({ defaults }, (
  { closeModal, installerId }: { closeModal: () => void; installerId?: string }
) => {
  // Call hooks and parent contexts
  const { formState } = useForm<CreatePortfolioFormState>();
  const history = useHistory();
  const { loggedInUser } = useUserContext();
  const isAdmin = Boolean(loggedInUser?.isAdmin);
  const isInstallerMissingForAdmin = isAdmin && !installerId;

  // Fetch installers so we can show the admin which installer this portfolio
  // will be created for.
  const { data: installers = [] } = useQuery({
    queryKey: ['admin', 'installers'],
    queryFn: () => getInstallers(),
    enabled: isAdmin && Boolean(installerId),
  });
  const selectedInstallerName = useMemo(() => {
    if (!installerId) return null;
    const match = (installers as { id: string; company?: { name?: string } }[])
      .find((inst) => inst.id === installerId);
    return match?.company?.name ?? null;
  }, [installers, installerId]);

  const { mutateAsync: createNewPortfolio, status: createNewPortfolioStatus } = useMutation(async () =>
    await createPortfolio({ ...formState, ...(installerId && { installerId }) })
  );

  // Form is not valid if the name or description is empty
  const hasError =
    !formState?.name ||
    !formState?.description ||
    !formState?.projectIds?.length ||
    isInstallerMissingForAdmin;

  // Handle the form submission
  const handleSubmit = async () => {
    if (!hasError) {
      try {
        const portfolio = await createNewPortfolio();
        toast.success('Portfolio created successfully');
        history.push(`/portfolio/${portfolio.id}`);
        closeModal();
      } catch (err) {
        console.error('Error creating portfolio:', err);
        toast.error('Error creating the portfolio');
      }
    };
  };

  // Get the list of projects that can be added to the portfolio. When an
  // installerId is provided (admin flow), scope the options to that installer
  // so admins don't see unrelated projects from the entire system.
  const { data: projects } = useProjectList();
  const scopedProjects = useMemo(() => {
    if (!projects) return projects;
    if (!installerId) return projects;
    return (projects as Project[]).filter(
      (project) => project.installerId === installerId
    );
  }, [projects, installerId]);
  const projectOptions = useMemo(
    () => getAvailableProjectOptions(scopedProjects),
    [scopedProjects]
  );

  // If there are no projects, show a message explaining that you need at least two projects to createNewPortfolio a portfolio
  if (!isInstallerMissingForAdmin && (!projectOptions.length || projectOptions.length < 2)) {
    return (
      <Modal closeModal={createNewPortfolioStatus !== 'loading' ? closeModal : undefined}>
        <Modal.Title>
          Create new portfolio
        </Modal.Title>
        <Modal.Content>
          <p>
            Portfolios are a great way to link together related projects so that they can be sent as a group to a single investor.
          </p>
          {isAdmin && installerId && (
            <div
              style={{
                padding: 'var(--small) var(--medium)',
                marginBottom: 'var(--medium)',
                border: '1px solid var(--color-neutral-200, #e0e0e0)',
                borderRadius: 4,
                background: 'var(--color-neutral-50, #fafafa)',
                fontSize: '0.95rem',
              }}
            >
              <strong>Installer:</strong>{' '}
              {selectedInstallerName ?? 'Loading installer…'}
            </div>
          )}
          <p>
            <strong>
              In order to create a portfolio, {installerId ? 'this installer needs' : 'you need'} to have at least two active projects.
            </strong>{' '}
            {installerId
              ? 'Please create additional projects for this installer before building a portfolio.'
              : 'Please return to the dashboard to create a new project.'}
          </p>
        </Modal.Content>
        <Modal.Footer>
          <Button primary onClick={closeModal}>Go back</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Otherwise, show the form
  return (
    <Modal>
      <Modal.Title>
        Create new portfolio
      </Modal.Title>
      <Modal.Content>
        <p>
          Portfolios are a great way to link together related projects so that they can be sent as a group to a single investor.
        </p>
        <p>
          To get started, enter a portfolio name, some details on how the constituent projects will be linked together, and at least two active projects.
        </p>
        {isAdmin && installerId && (
          <div
            style={{
              padding: 'var(--small) var(--medium)',
              marginBottom: 'var(--medium)',
              border: '1px solid var(--color-neutral-200, #e0e0e0)',
              borderRadius: 4,
              background: 'var(--color-neutral-50, #fafafa)',
              fontSize: '0.95rem',
            }}
          >
            <strong>Installer:</strong>{' '}
            {selectedInstallerName ?? 'Loading installer…'}
          </div>
        )}
        <Form onSubmit={handleSubmit}>
          <FormSchemaAwareWidget
            fieldLabel="Portfolio name"
            placeholder="Enter value"
            schemaKey="name"
          />
          {/* @ts-ignore */}
          <AddProjectsDropdown projectOptions={projectOptions} />
          <FormSchemaAwareWidget
            as={TextArea}
            fieldLabel="Portfolio description"
            placeholder="Please use this space to describe how the projects relate to each other and provide high-level details about the portfolio."
            schemaKey="description"
          />
          {isInstallerMissingForAdmin && (
            <p style={{ color: '#b00020', marginTop: '0.5rem' }}>
              An installer must be selected before creating a portfolio. Please go back and choose an installer.
            </p>
          )}
        </Form>
      </Modal.Content>
      <Modal.Footer>
        <Button primary outlined onClick={closeModal}>Cancel</Button>
        <Button
          primary
          disabled={hasError || createNewPortfolioStatus === 'loading'}
          loading={createNewPortfolioStatus === 'loading'}
          onClick={handleSubmit}
        >
          Create portfolio
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

export default CreatePortfolioModal;